import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useBranding } from '@/context/BrandingContext';

const THEME_KEY = 'fpccs-theme-mode';
const THEMES = ['light', 'dark', 'system'];
const ThemeContext = createContext({ mode: 'system', resolvedTheme: 'dark', profile: 'modern', setMode: () => {} });

function systemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const { branding } = useBranding();
  const [mode, setModeState] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return THEMES.includes(saved) ? saved : 'system';
  });
  const [system, setSystem] = useState(systemTheme);
  const resolvedTheme = mode === 'system' ? system : mode;
  const profile = branding.theme_profile === 'forte' ? 'forte' : 'modern';

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const change = () => setSystem(query.matches ? 'dark' : 'light');
    query.addEventListener?.('change', change);
    return () => query.removeEventListener?.('change', change);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(THEME_KEY) && branding.default_theme) {
      setModeState(branding.default_theme);
    }
  }, [branding.default_theme]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.dataset.themeProfile = profile;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [profile, resolvedTheme]);

  const setMode = (next) => {
    const safe = THEMES.includes(next) ? next : 'system';
    localStorage.setItem(THEME_KEY, safe);
    setModeState(safe);
  };
  const value = useMemo(() => ({ mode, resolvedTheme, profile, setMode }), [mode, profile, resolvedTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
